import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ExternalLink, Copy, MousePointerClick } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Panel, TextInput, TextArea, EmptyState } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function LinkManager() {
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, "links");
  const [links, setLinks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ key: "", label: "", url: "", category: "General", description: "" });

  const load = () => api.get("/links").then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ key: "", label: "", url: "", category: "External • Custom", description: "" }); setEditing("new"); };
  const openEdit = (l) => { setForm({ key: l.key, label: l.label, url: l.url, category: l.category, description: l.description }); setEditing(l.id); };

  const save = async () => {
    if (!form.key || !form.label) { toast.error("Key and label are required"); return; }
    try {
      if (editing === "new") await api.post("/links", form);
      else await api.put(`/links/${editing}`, form);
      toast.success("Link saved — propagated across the site");
      setEditing(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/links/${confirm.id}`); toast.success("Link deleted"); setConfirm(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const groups = links.reduce((acc, l) => { (acc[l.category] = acc[l.category] || []).push(l); return acc; }, {});

  return (
    <div>
      <AdminHeader
        title="Central Link Manager"
        subtitle="Single source of truth for every external system URL. Update once — it propagates everywhere on the site."
        action={canEdit && <button onClick={openNew} className="btn-gold px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="add-link-btn"><Plus size={16} /> Add Link</button>}
      />

      {links.length === 0 ? <Panel><EmptyState>No links yet.</EmptyState></Panel> : (
        <div className="space-y-6">
          {Object.entries(groups).map(([cat, items]) => (
            <div key={cat}>
              <h3 className="overline mb-3">{cat}</h3>
              <Panel>
                <div className="divide-y divide-[#203E70]">
                  {items.map((l) => (
                    <div key={l.id} className="flex items-center gap-4 p-4" data-testid={`link-row-${l.key}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium">{l.label}</span>
                          <code className="text-xs text-slate-500 font-mono">{l.key}</code>
                        </div>
                        <p className={`text-xs mt-1 font-mono truncate ${l.url && l.url !== "#" ? "text-slate-400" : "text-amber-500/80"}`}>
                          {l.url && l.url !== "#" ? l.url : "⚠ placeholder — set a real URL"}
                        </p>
                      </div>
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500 shrink-0"><MousePointerClick size={13} /> {l.clicks || 0}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {l.url && l.url !== "#" && (
                          <>
                            <button onClick={() => { navigator.clipboard.writeText(l.url); toast.success("URL copied"); }} className="p-2 text-slate-400 hover:text-[#D8AE4E]" title="Copy URL" data-testid={`link-copy-${l.key}`}><Copy size={15} /></button>
                            <a href={l.url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-[#D8AE4E]" title="Open" data-testid={`link-open-${l.key}`}><ExternalLink size={15} /></a>
                          </>
                        )}
                        {canEdit && <button onClick={() => openEdit(l)} className="p-2 text-slate-400 hover:text-[#D8AE4E]" data-testid={`link-edit-${l.key}`}><Pencil size={15} /></button>}
                        {canEdit && <button onClick={() => setConfirm(l)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`link-delete-${l.key}`}><Trash2 size={15} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0B1D40] border-[#203E70] text-white">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "Add Link" : "Edit Link"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <TextInput label="Key (used in code — no spaces)" value={form.key} disabled={editing !== "new"} onChange={(e) => setForm({ ...form, key: e.target.value.replace(/\s+/g, "_").toLowerCase() })} testid="link-form-key" />
            <TextInput label="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} testid="link-form-label" />
            <TextInput label="URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} hint="The external system this button/link points to" testid="link-form-url" />
            <TextInput label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} testid="link-form-category" />
            <TextArea label="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} testid="link-form-desc" />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold px-4 py-2 text-sm">Cancel</button>
            <button type="button" onClick={save} className="btn-gold px-4 py-2 text-sm" data-testid="link-form-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0B1D40] border-[#203E70] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete link?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">"{confirm?.label}" will be removed. Any buttons using key <code className="text-[#D8AE4E]">{confirm?.key}</code> will stop working until reconfigured.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#203E70] text-white hover:bg-[#102653]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white" data-testid="link-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
